/**
 * Custom Output Tables designer (super-admin)
 *
 * Build block-based "scan sheet" tables rendered below the BOM / placement
 * guide: a card template (rows of Bootstrap-column cells, each a stack of typed
 * blocks — text line, barcode, qty badge, spacer with per-block styling) that
 * repeats per product. Live preview renders through the same CustomBlockTable
 * the real estimate uses.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, X, Eye, Barcode } from 'lucide-react';
import {
  BarcodeSymbology, CellBlock, CellBlockType, CustomTableCard, CustomTableCell,
  CustomTablePlacement, CustomTableDataSource, MaterialItem, Product, ProductCustomField,
} from '../../types';
import { BARCODE_SYMBOLOGIES } from '../../utils/barcode';
import { buildCustomTablePreview, RenderCtx, BUILTIN_CODE_SOURCES } from '../../utils/renderModel';
import { CustomBlockTable } from '../../components/results/CustomBlockTable';
import { fetchProductsPayload } from '../../services/api';
import {
  AdminCustomTable, CustomTableRetailerSummary, CustomTablePayload,
  fetchCustomTables, createCustomTable, updateCustomTable, deleteCustomTable,
} from '../../services/adminCustomTablesApi';

const uid = () => `b${Math.random().toString(36).slice(2, 9)}`;

interface Draft extends CustomTablePayload { id: number | null; }

const blankCard = (): CustomTableCard => ({
  cells: [
    { colSpan: 8, blocks: [
      { id: uid(), type: 'textLine', text: '{name}', style: { fontWeight: 'bold', fontSizePx: 13 } },
      { id: uid(), type: 'textLine', text: '{productNumber}', style: { fontSizePx: 10, color: '#6b7280' } },
      { id: uid(), type: 'qtyBadge' },
    ] },
    { colSpan: 4, blocks: [
      { id: uid(), type: 'barcode', style: { align: 'center' }, barcode: { symbology: 'code128', sourceFieldKey: 'productNumber', heightPx: 40 } },
    ] },
  ],
});

const blankDraft = (): Draft => ({
  id: null,
  retailerId: null,
  // Internal unique id for the table — auto-generated, never shown to the user.
  key: `t${Date.now().toString(36)}`,
  title: 'In-Store Scan Sheet',
  placement: 'belowBom',
  dataSource: 'bom',
  cardsPerRow: 2,
  card: blankCard(),
  visibleBrowser: true,
  visiblePrint: true,
  visiblePdf: true,
  isActive: true,
  sortOrder: 0,
});

const BLOCK_TYPES: { value: CellBlockType; label: string }[] = [
  { value: 'textLine', label: 'Text line' },
  { value: 'barcode', label: 'Barcode' },
  { value: 'qtyBadge', label: 'Qty badge' },
  { value: 'spacer', label: 'Spacer' },
];

export const AdminCustomTables: React.FC = () => {
  const [tables, setTables] = useState<AdminCustomTable[]>([]);
  const [retailers, setRetailers] = useState<CustomTableRetailerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Sample data for the live preview (selected retailer's catalog).
  const [previewProducts, setPreviewProducts] = useState<Product[]>([]);
  const [availableFields, setAvailableFields] = useState<{ fieldKey: string; label: string }[]>([]);

  const reload = () =>
    fetchCustomTables()
      .then((res) => { setTables(res.tables); setRetailers(res.retailers); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => { reload(); }, []);

  useEffect(() => {
    let cancelled = false;
    fetchProductsPayload(draft?.retailerId ?? 1)
      .then((res) => { if (!cancelled) { setPreviewProducts(res.products ?? []); setAvailableFields(res.availableFields ?? []); } })
      .catch(() => { if (!cancelled) { setPreviewProducts([]); setAvailableFields([]); } });
    return () => { cancelled = true; };
  }, [draft?.retailerId]);

  // Any active field (even non-output) can be a barcode/token source. Show the
  // field label AND key so fields that share a column heading are distinguishable.
  const sourceFieldOptions = useMemo(() => {
    const opts = [{ key: 'productNumber', label: 'Product # (Trusscore SKU)' }];
    BUILTIN_CODE_SOURCES.forEach((c) => opts.push({ key: c.key, label: c.label }));
    availableFields.forEach((f) => opts.push({ key: f.fieldKey, label: `${f.label} (${f.fieldKey})` }));
    return opts;
  }, [availableFields]);

  const getFields = useMemo(
    () => (productID: string): ProductCustomField[] =>
      previewProducts.find((p) => p.productID === productID)?.customFields ?? [],
    [previewProducts],
  );

  const sampleItems: MaterialItem[] = useMemo(() => {
    const qty = [12, 5, 8];
    return previewProducts.slice(0, 3).map((p, i) => ({
      productNumber: p.productNumber, productID: p.productID, name: p.name, color: p.color,
      quantity: qty[i] ?? 6, lengthFeet: p.lengthFeet, sqft: p.sqft, type: 'WCB_Surface',
    }));
  }, [previewProducts]);

  const previewTable = useMemo(() => {
    if (!draft) return null;
    const ctx: RenderCtx = { currency: 'USD', showPrices: false, getFields };
    return buildCustomTablePreview(draft as any, sampleItems, ctx);
  }, [draft, sampleItems, getFields]);

  const setCard = (card: CustomTableCard) => setDraft((d) => (d ? { ...d, card } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true); setError(null); setNotice(null);
    try {
      const payload: CustomTablePayload = {
        retailerId: draft.retailerId, key: draft.key.trim(), title: draft.title,
        placement: draft.placement, dataSource: draft.dataSource, cardsPerRow: draft.cardsPerRow,
        card: draft.card,
        // For 'static', render the single template card once (no data tokens).
        staticCards: draft.dataSource === 'static' ? [draft.card] : undefined,
        visibleBrowser: draft.visibleBrowser, visiblePrint: draft.visiblePrint, visiblePdf: draft.visiblePdf,
        isActive: draft.isActive, sortOrder: draft.sortOrder,
      };
      if (draft.id) await updateCustomTable(draft.id, payload);
      else await createCustomTable(payload);
      await reload();
      setDraft(null);
      setNotice('Custom table saved.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this custom table?')) return;
    try { await deleteCustomTable(id); await reload(); setDraft(null); setNotice('Deleted.'); }
    catch (e) { setError(e instanceof Error ? e.message : 'Delete failed'); }
  };

  const editExisting = (t: AdminCustomTable) => {
    setDraft({
      id: t.id, retailerId: t.retailerId, key: t.key, title: t.title, placement: t.placement,
      dataSource: t.dataSource, cardsPerRow: t.cardsPerRow, card: t.card,
      visibleBrowser: t.visibleBrowser, visiblePrint: t.visiblePrint, visiblePdf: t.visiblePdf,
      isActive: t.isActive ?? true, sortOrder: t.sortOrder ?? 0,
    });
    setNotice(null); setError(null);
  };

  if (loading) return <div className="p-4">Loading custom tables…</div>;

  return (
    <div className="admin-custom-tables">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="h4 mb-1">Custom Output Tables</h2>
          <p className="text-muted small mb-0">Block-based "scan sheet" tables rendered below the BOM or placement guide.</p>
        </div>
        {!draft && (
          <button className="btn btn-primary btn-sm" onClick={() => setDraft(blankDraft())}>
            <Plus size={15} className="me-1" /> New table
          </button>
        )}
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {notice && <div className="alert alert-success py-2">{notice}</div>}

      {/* List */}
      {!draft && (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead><tr><th>Title</th><th>Applies to</th><th>Placement</th><th>Source</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {tables.length === 0 && <tr><td colSpan={6} className="text-muted">No custom tables yet.</td></tr>}
              {tables.map((t) => (
                <tr key={t.id}>
                  <td>{t.title || <em className="text-muted">untitled</em>}</td>
                  <td>{t.retailerId === null ? 'Universal' : (retailers.find((r) => r.id === t.retailerId)?.name ?? `#${t.retailerId}`)}</td>
                  <td>{t.placement === 'belowBom' ? 'Below BOM' : 'Below placement'}</td>
                  <td>{t.dataSource}</td>
                  <td>{t.isActive ? 'Yes' : 'No'}</td>
                  <td className="text-end">
                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => editExisting(t)}>Edit</button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => remove(t.id)}><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor */}
      {draft && (
        <div className="row g-4">
          <div className="col-lg-7">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">{draft.id ? 'Edit table' : 'New table'}</h5>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setDraft(null)} disabled={saving}>
                  <X size={15} className="me-1" /> Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  <Save size={15} className="me-1" /> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            {/* Table-level settings */}
            <div className="row g-2 mb-3">
              <div className="col-sm-8">
                <label className="form-label small fw-semibold">Applies to</label>
                <select className="form-select form-select-sm" value={draft.retailerId === null ? 'universal' : String(draft.retailerId)}
                  onChange={(e) => setDraft({ ...draft, retailerId: e.target.value === 'universal' ? null : Number(e.target.value) })}>
                  <option value="universal">Universal (all retailers)</option>
                  {retailers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className="col-sm-4">
                <label className="form-label small fw-semibold">Cards / row</label>
                <input type="number" min={1} max={4} className="form-control form-control-sm" value={draft.cardsPerRow}
                  onChange={(e) => setDraft({ ...draft, cardsPerRow: Math.max(1, Math.min(4, parseInt(e.target.value, 10) || 2)) })} />
              </div>
              <div className="col-sm-6">
                <label className="form-label small fw-semibold">Title</label>
                <input className="form-control form-control-sm" value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div className="col-sm-3">
                <label className="form-label small fw-semibold">Placement</label>
                <select className="form-select form-select-sm" value={draft.placement}
                  onChange={(e) => setDraft({ ...draft, placement: e.target.value as CustomTablePlacement })}>
                  <option value="belowBom">Below BOM</option>
                  <option value="belowPlacement">Below placement</option>
                </select>
              </div>
              <div className="col-sm-3">
                <label className="form-label small fw-semibold">Data source</label>
                <select className="form-select form-select-sm" value={draft.dataSource}
                  onChange={(e) => setDraft({ ...draft, dataSource: e.target.value as CustomTableDataSource })}>
                  <option value="bom">Per BOM product</option>
                  <option value="surfaces">Per surface product</option>
                  <option value="static">Static (no repeat)</option>
                </select>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-3 mb-3">
              {(['visibleBrowser', 'visiblePrint', 'visiblePdf'] as const).map((k) => (
                <div className="form-check form-switch" key={k}>
                  <input type="checkbox" className="form-check-input" role="switch" id={`ct-${k}`}
                    checked={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} />
                  <label className="form-check-label small" htmlFor={`ct-${k}`}>
                    {k === 'visibleBrowser' ? 'Web' : k === 'visiblePrint' ? 'Print' : 'PDF'}
                  </label>
                </div>
              ))}
              {draft.id && (
                <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => remove(draft.id!)}>
                  <Trash2 size={13} className="me-1" /> Delete
                </button>
              )}
            </div>

            {/* Card template editor */}
            <CardEditor card={draft.card} sourceFieldOptions={sourceFieldOptions} onChange={setCard} />
            <p className="text-muted small mt-2">
              Text tokens: <code>{'{name}'}</code> <code>{'{productNumber}'}</code> <code>{'{qty}'}</code>{' '}
              <code>{'{color}'}</code> <code>{'{linearFt}'}</code> <code>{'{sqft}'}</code>{' '}
              <code>{'{upc}'}</code> <code>{'{gtin}'}</code> <code>{'{ean}'}</code> <code>{'{gtin8}'}</code>{' '}
              and any custom field key.
            </p>
          </div>

          {/* Live preview */}
          <div className="col-lg-5">
            <div className="card">
              <div className="card-header d-flex align-items-center gap-2 py-2">
                <Eye size={15} /> <span className="fw-semibold small">Live preview</span>
              </div>
              <div className="card-body">
                {previewTable ? <CustomBlockTable table={previewTable} /> : <span className="text-muted small">—</span>}
                <p className="text-muted small mb-0 mt-2">Sample data from the selected retailer's catalog.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Card / cell / block editors ──────────────────────────────────────────────

const CardEditor: React.FC<{
  card: CustomTableCard;
  sourceFieldOptions: { key: string; label: string }[];
  onChange: (c: CustomTableCard) => void;
}> = ({ card, sourceFieldOptions, onChange }) => {
  const setCell = (i: number, cell: CustomTableCell) =>
    onChange({ cells: card.cells.map((c, idx) => (idx === i ? cell : c)) });
  const addCell = () => onChange({ cells: [...card.cells, { colSpan: 6, blocks: [] }] });
  const removeCell = (i: number) => onChange({ cells: card.cells.filter((_, idx) => idx !== i) });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
        <label className="form-label small fw-semibold mb-0">Card layout (cells use a 12-column grid)</label>
        <div className="d-flex align-items-center gap-2">
          <span className="small text-muted">Card background</span>
          <input
            type="color"
            className="form-control form-control-color form-control-sm"
            style={{ width: 36 }}
            value={card.background || '#ffffff'}
            onChange={(e) => onChange({ ...card, background: e.target.value })}
            title="Whole-card background"
          />
          <button className="btn btn-sm btn-outline-primary" onClick={addCell}><Plus size={13} /> Cell</button>
        </div>
      </div>
      {card.cells.map((cell, i) => (
        <div key={i} className="border rounded-3 p-2 mb-2 bg-light">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="small fw-semibold">Cell {i + 1}</span>
            <span className="small text-muted">width (of 12)</span>
            <input type="number" min={1} max={12} className="form-control form-control-sm" style={{ width: 64 }}
              value={cell.colSpan} onChange={(e) => setCell(i, { ...cell, colSpan: Math.max(1, Math.min(12, parseInt(e.target.value, 10) || 6)) })} />
            <span className="small text-muted">bg</span>
            <input type="color" className="form-control form-control-color form-control-sm" style={{ width: 36 }}
              value={cell.background || '#ffffff'} onChange={(e) => setCell(i, { ...cell, background: e.target.value })} title="Cell background" />
            <button className="btn btn-sm btn-link text-danger ms-auto p-0" onClick={() => removeCell(i)}><Trash2 size={14} /></button>
          </div>
          <CellBlocksEditor cell={cell} sourceFieldOptions={sourceFieldOptions} onChange={(c) => setCell(i, c)} />
        </div>
      ))}
    </div>
  );
};

const CellBlocksEditor: React.FC<{
  cell: CustomTableCell;
  sourceFieldOptions: { key: string; label: string }[];
  onChange: (c: CustomTableCell) => void;
}> = ({ cell, sourceFieldOptions, onChange }) => {
  const setBlock = (i: number, b: CellBlock) => onChange({ ...cell, blocks: cell.blocks.map((x, idx) => (idx === i ? b : x)) });
  const addBlock = (type: CellBlockType) => {
    const b: CellBlock = type === 'barcode'
      ? { id: uid(), type, style: { align: 'center' }, barcode: { symbology: 'code128', sourceFieldKey: 'productNumber', heightPx: 40 } }
      : type === 'textLine' ? { id: uid(), type, text: '{name}' }
      : { id: uid(), type };
    onChange({ ...cell, blocks: [...cell.blocks, b] });
  };
  const removeBlock = (i: number) => onChange({ ...cell, blocks: cell.blocks.filter((_, idx) => idx !== i) });
  const moveBlock = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= cell.blocks.length) return;
    const next = [...cell.blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange({ ...cell, blocks: next });
  };

  return (
    <div>
      {cell.blocks.map((b, i) => (
        <BlockEditor
          key={b.id}
          block={b}
          sourceFieldOptions={sourceFieldOptions}
          isFirst={i === 0}
          isLast={i === cell.blocks.length - 1}
          onChange={(nb) => setBlock(i, nb)}
          onRemove={() => removeBlock(i)}
          onMove={(d) => moveBlock(i, d)}
        />
      ))}
      <div className="btn-group btn-group-sm mt-1">
        {BLOCK_TYPES.map((t) => (
          <button key={t.value} className="btn btn-outline-secondary" onClick={() => addBlock(t.value)}>+ {t.label}</button>
        ))}
      </div>
    </div>
  );
};

const BlockEditor: React.FC<{
  block: CellBlock;
  sourceFieldOptions: { key: string; label: string }[];
  isFirst: boolean;
  isLast: boolean;
  onChange: (b: CellBlock) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}> = ({ block, sourceFieldOptions, isFirst, isLast, onChange, onRemove, onMove }) => {
  const style = block.style ?? {};
  const setStyle = (patch: Partial<NonNullable<CellBlock['style']>>) => onChange({ ...block, style: { ...style, ...patch } });

  return (
    <div className="border rounded-2 p-2 mb-1 bg-white">
      <div className="d-flex align-items-center gap-2 mb-1">
        <span className="badge text-bg-secondary text-capitalize">{block.type}</span>
        <div className="btn-group btn-group-sm ms-auto">
          <button className="btn btn-outline-secondary" onClick={() => onMove(-1)} disabled={isFirst}><ArrowUp size={12} /></button>
          <button className="btn btn-outline-secondary" onClick={() => onMove(1)} disabled={isLast}><ArrowDown size={12} /></button>
          <button className="btn btn-outline-danger" onClick={onRemove}><Trash2 size={12} /></button>
        </div>
      </div>

      {block.type === 'textLine' && (
        <input className="form-control form-control-sm mb-1" value={block.text ?? ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })} placeholder="{name}" />
      )}

      {block.type === 'qtyBadge' && (
        <input className="form-control form-control-sm mb-1" value={block.text ?? ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })} placeholder="Qty: {qty}" />
      )}

      {block.type === 'barcode' && (() => {
        const bc = block.barcode ?? { symbology: 'code128' as BarcodeSymbology, sourceFieldKey: 'productNumber', heightPx: 40 };
        const setBc = (patch: Partial<NonNullable<CellBlock['barcode']>>) => onChange({ ...block, barcode: { ...bc, ...patch } });
        const hint: Record<string, string> = {
          upca: 'UPC-A needs an 11- or 12-digit numeric value.',
          ean13: 'EAN-13 needs a 12- or 13-digit numeric value.',
          ean8: 'EAN-8 needs a 7- or 8-digit numeric value.',
          code128: 'Code 128 accepts any letters/numbers (best for SKUs).',
        };
        return (
          <>
            <div className="d-flex flex-wrap gap-1 mb-1 align-items-center">
              <select className="form-select form-select-sm" style={{ maxWidth: 150 }} value={bc.symbology}
                onChange={(e) => setBc({ symbology: e.target.value as BarcodeSymbology })}>
                {BARCODE_SYMBOLOGIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <span className="small text-muted">encode</span>
              <select className="form-select form-select-sm" style={{ maxWidth: 150 }} value={bc.sourceFieldKey}
                onChange={(e) => setBc({ sourceFieldKey: e.target.value })}>
                {sourceFieldOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
              <span className="small text-muted">align</span>
              <select className="form-select form-select-sm" style={{ width: 90 }} value={style.align ?? 'center'}
                onChange={(e) => setStyle({ align: e.target.value as 'left' | 'center' | 'right' })}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
              <span className="small text-muted">bar height px</span>
              <input type="number" className="form-control form-control-sm" style={{ width: 70 }} min={20} max={400}
                value={bc.heightPx ?? 40} onChange={(e) => setBc({ heightPx: parseInt(e.target.value, 10) || 40 })} />
            </div>
            <div className="form-text" style={{ fontSize: '0.7em' }}>{hint[bc.symbology]}</div>
          </>
        );
      })()}

      {/* Style controls (text/badge/spacer) */}
      {block.type !== 'barcode' && (
        <div className="d-flex flex-wrap align-items-center gap-1">
          {block.type !== 'spacer' && (
            <>
              <select className="form-select form-select-sm" style={{ width: 90 }} value={style.fontWeight ?? 'normal'}
                onChange={(e) => setStyle({ fontWeight: e.target.value as 'normal' | 'bold' })}>
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
              <span className="small text-muted">color</span>
              <input type="color" className="form-control form-control-color form-control-sm" style={{ width: 34 }}
                value={style.color || '#000000'} onChange={(e) => setStyle({ color: e.target.value })} />
              <span className="small text-muted">bg</span>
              <input type="color" className="form-control form-control-color form-control-sm" style={{ width: 34 }}
                value={style.background || '#ffffff'} onChange={(e) => setStyle({ background: e.target.value })} />
              <select className="form-select form-select-sm" style={{ width: 90 }} value={style.align ?? 'left'}
                onChange={(e) => setStyle({ align: e.target.value as 'left' | 'center' | 'right' })}>
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </>
          )}
          <span className="small text-muted">{block.type === 'spacer' ? 'height px' : 'font px'}</span>
          <input type="number" className="form-control form-control-sm" style={{ width: 64 }} min={4} max={72}
            value={style.fontSizePx ?? (block.type === 'spacer' ? 8 : 12)}
            onChange={(e) => setStyle({ fontSizePx: parseInt(e.target.value, 10) || 12 })} />
        </div>
      )}
    </div>
  );
};

export default AdminCustomTables;
